import {
  Box,
  Grid,
  Typography,
  Switch,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import LayersIcon from '@mui/icons-material/Layers';
import { PNG } from 'components/Asset/PNG';
import { SVG } from 'components/Asset/SVG';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import TireStatusLegend from 'components/TireStatusLegend/TireStatusLegend';
import TireProfileHandler from 'handlers/tire-pressure';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hook';
import { truncateTo2dp } from 'utils/helpers/general';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { convertKpaToPsi, convertCentToFahr } from 'utils/helpers/unitSystem';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { isDarkTheme } from 'utils/theme';
import FleetDashboardView from 'views/TirePressure/Components/FleetDashboardView';
import CombinedFourWheels from 'views/TirePressure/Components/CombinedSchematics/CombinedFourWheels';
import CombinedSixWheels from 'views/TirePressure/Components/CombinedSchematics/CombinedSixWheels';
import CombinedFourSixWheels from 'views/TirePressure/Components/CombinedSchematics/CombinedFourSixWheels';
import CombinedThreeAxlesSixWheels from 'views/TirePressure/Components/CombinedSchematics/CombinedThreeAxlesSixWheels';
import CombinedThreeAxlesTenWheels from 'views/TirePressure/Components/CombinedSchematics/CombinedThreeAxlesTenWheels';
import {
  getLastUpdatedTimestamp,
  getDynamicAssetTireInfo,
  getOrderedTireCodes,
} from './util';
import { type Asset } from 'types/asset.types';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { TireStatus, determineTireStatus } from 'utils/tpms';
import { ReportDownload } from './Components/ReportDownload/ReportDownload';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
const FleetAlertSummary = lazy(
  async () => await import('./Components/FleetAlertSummary')
);

const FleetInsight = () => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const tireProfileHandler = new TireProfileHandler();
  const navigate = useNavigate();

  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const { assets, assetsLoaded } = useAppSelector(
    (state) => state.assetReducer
  );
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const {
    axleWiseLimits,
    fleetData,
    isLoadingFLeet,
    dashboard,
    isLoadingDashboard,
    batteryData,
  } = useAppSelector((state) => state.tireProfileReducer);
  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);

  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [searchValue, setSearchValue] = useState<string>('');
  const [assetGrouping, setAssetGrouping] = useState<string>('All');
  const [metricType] = useState<'pressure' | 'temperature'>('pressure');
  const [viewMode, setViewMode] = useState<'dashboard' | 'combined'>(
    'combined'
  );
  const [alertFilter, setAlertFilter] = useState<
    'leakage' | 'lowPressure' | 'highPressure' | 'highTemp' | null
  >(null);

  const fleetList = useMemo(() => {
    try {
      if (!assets || !Array.isArray(assets)) {
        console.error('Assets data is invalid or undefined');
        return [];
      }

      const assetsWithCounts = assets
        .filter((item) => item.tpmsEnabled)
        .map((asset) => {
          const deviceId = asset.device?.deviceId;
          const itemData =
            deviceId != null ? fleetData?.[metricType]?.[deviceId] ?? {} : {};
          const pressureData =
            deviceId != null ? fleetData?.pressure?.[deviceId] ?? {} : {};

          // Get asset tire details
          const allAssetTires = fleetData?.assetTires ?? [];
          const assetTires = allAssetTires.filter(
            (at) => at.assetId === asset.assetId
          );
          const assetTireDetails = getDynamicAssetTireInfo(
            makeModelDetails,
            tirePatterns.tirePatternPositions,
            asset.make,
            asset.model,
            customerId
          );
          const itemPattern = asset.patternName ?? assetTireDetails.patternName;
          // Resolve per-tireCode axle-wise limits for this asset's make/model
          const makeModelEntry = axleWiseLimits.find(
            (e: any) => e.make === asset.make && e.model === asset.model
          );
          const tireLimitsForAsset: any[] = makeModelEntry?.positions ?? [];
          const dataKeys = Object.keys(itemData);
          const orderedKeys = getOrderedTireCodes(dataKeys, itemPattern);

          // Check if asset has any red tires
          let hasUnhealthyTire = false;
          const pressureTireStatusMap: Record<string, TireStatus> = {};
          const temperatureTireStatusMap: Record<string, TireStatus> = {};
          const tireStaleMap: Record<string, boolean> = {};

          // Always access both pressure and temperature data for the combined view
          const temperatureData =
            deviceId != null ? fleetData?.temperature?.[deviceId] ?? {} : {};

          const computeTireStatus = (
            hasSensor: boolean,
            entry: any,
            tireLimit: any,
            metric: 'pressure' | 'temperature'
          ): TireStatus => {
            if (!hasSensor) return TireStatus.NO_SENSOR;
            const rawVal = entry?.value;
            if (
              !entry ||
              Object.values(entry).length === 0 ||
              entry.ts === null ||
              rawVal === null ||
              rawVal === undefined
            ) {
              return TireStatus.NO_DATA;
            }
            const displayVal = truncateTo2dp(rawVal);
            let maxValue, minValue, warningValue;
            if (metric === 'pressure') {
              maxValue =
                unitSystem === 'metric'
                  ? tireLimit.maxPressure
                  : convertKpaToPsi(tireLimit?.maxPressure);
              minValue =
                unitSystem === 'metric'
                  ? tireLimit.minPressure
                  : convertKpaToPsi(tireLimit?.minPressure);
              warningValue =
                unitSystem === 'metric'
                  ? tireLimit.warningPressure
                  : convertKpaToPsi(tireLimit?.warningPressure);
            } else {
              maxValue =
                unitSystem === 'metric'
                  ? tireLimit.maxTemperature
                  : convertCentToFahr(tireLimit?.maxTemperature);
              minValue =
                unitSystem === 'metric'
                  ? tireLimit.minTemperature
                  : convertCentToFahr(tireLimit?.minTemperature);
              warningValue =
                unitSystem === 'metric'
                  ? tireLimit.warningTemperature
                  : convertCentToFahr(tireLimit?.warningTemperature);
            }
            return determineTireStatus(
              Number(displayVal),
              { min: minValue, max: maxValue, warning: warningValue },
              !!assetTireDetails,
              metric
            );
          };

          orderedKeys.forEach((key, index) => {
            const itemKeyHasSensor = assetTires.find(
              (atItem) => atItem?.tireCode === key
            );
            const hasSensor = !!itemKeyHasSensor;

            const pEntry = (pressureData as any)[key];
            const tEntry = (temperatureData as any)[key];

            const tireLimit =
              tireLimitsForAsset.find(
                (itemLimit: any) => itemLimit.tireCode === key
              ) || {};

            const pStatus = computeTireStatus(
              hasSensor,
              pEntry,
              tireLimit,
              'pressure'
            );
            const tStatus = computeTireStatus(
              hasSensor,
              tEntry,
              tireLimit,
              'temperature'
            );

            pressureTireStatusMap[`p${index + 1}`] = pStatus;
            temperatureTireStatusMap[`p${index + 1}`] = tStatus;

            // Calculate per-tire staleness based on UTC timestamp
            const currentValue = metricType === 'pressure' ? pEntry : tEntry;
            const isTireStale = (() => {
              if (!currentValue?.ts) return false;
              const hoursDiff =
                (Date.now() - currentValue.ts) / (1000 * 60 * 60);
              return hoursDiff > 24;
            })();
            tireStaleMap[`p${index + 1}`] = isTireStale;

            if (
              pStatus === TireStatus.UNHEALTHY ||
              tStatus === TireStatus.UNHEALTHY
            ) {
              hasUnhealthyTire = true;
            }
          });

          // tireStatusMap is based on the current metric data.
          const tireStatusMap =
            metricType === 'pressure'
              ? pressureTireStatusMap
              : temperatureTireStatusMap;

          return {
            ...asset,
            hasUnhealthyTire,
            tireStatusMap,
            pressureTireStatusMap,
            temperatureTireStatusMap,
            tireStaleMap,
          };
        });

      let fleetToProcess = assetsWithCounts;

      if (searchValue !== '') {
        fleetToProcess = fleetToProcess.filter(
          (asset) =>
            asset.assetType
              ?.toLowerCase()
              .includes(searchValue.toLowerCase()) ||
            asset.make?.toLowerCase().includes(searchValue.toLowerCase()) ||
            asset.model?.toLowerCase().includes(searchValue.toLowerCase()) ||
            asset.bumperNumber
              ?.toLowerCase()
              .includes(searchValue.toLowerCase())
        );
      }

      // Filter by assets with unhealthy tires only
      if (assetGrouping === 'Unhealthy Assets') {
        fleetToProcess = fleetToProcess.filter(
          (asset) => asset.hasUnhealthyTire
        );
      }

      // Sort by bumperNumber
      fleetToProcess.sort((a, b) =>
        a.bumperNumber.localeCompare(b.bumperNumber, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );

      return fleetToProcess;
    } catch (error) {
      console.error('Error filtering fleet list:', error);
      return [];
    }
  }, [
    assets,
    fleetData,
    dashboard,
    axleWiseLimits,
    unitSystem,
    searchValue,
    assetGrouping,
    metricType,
    makeModelDetails,
    tirePatterns,
    customerId,
  ]);

  useEffect(() => {
    if (accessToken) {
      void tireProfileHandler.getFleetTireProfile();
    }
  }, [accessToken]);

  // Lazy-fetch dashboard alert data only when dashboard view is first opened
  useEffect(() => {
    if (viewMode === 'dashboard' && accessToken && !dashboard?.notifications) {
      void tireProfileHandler.getDashboard();
    }
  }, [viewMode, accessToken]);

  // Fetch axle-wise limits (per tireCode thresholds) once we have auth + customer
  useEffect(() => {
    if (accessToken && customerId) {
      void tireProfileHandler.getAxleWiseLimits(customerId);
    }
  }, [accessToken, customerId]);

  // Fetch battery data explicitly once assets with devices are available
  useEffect(() => {
    // Prefer devices from fleet assets (ensures presence on first load)
    const devices = (assets || [])
      .filter((a: any) => a?.device?.deviceId && a?.assetType)
      .map((a: any) => ({
        deviceId: a.device.deviceId,
        assetType: a.assetType,
      }));
    if (devices.length > 0) {
      void tireProfileHandler.getBatteryDataForDevices(devices);
    }
  }, [assets]);

  const fontColor = palette.textPrimary;

  return (
    <Box
      sx={{
        marginTop: '20px',
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        padding: '10px',
        borderRadius: '4px',
      }}
    >
      {!assetsLoaded || isLoadingFLeet ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <SingleFadeLoader />
        </div>
      ) : (
        <>
          {/* Header area: search + switches + legend */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'nowrap',
            }}
          >
            {/* Left side: Search field */}
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <SWTextField
                name="filter"
                placeholder={t('search_for_an_asset')}
                TextFieldProps={{
                  sx: {
                    fontSize: '12px',
                    '& .MuiInputBase-root': {
                      height: '28px',
                      width: '200px',
                    },
                    input: {
                      color: palette.textPrimary,
                    },
                    ...(enableDarkTheme && {
                      border: `1px solid #2F445E}`,
                    }),
                    borderRadius: '3px',
                  },
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchValue(e.target.value);
                  },
                }}
                enableDarkTheme={enableDarkTheme}
              />
            </Box>

            {/* Center: Legend */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
                minWidth: 0,
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <TireStatusLegend
                metricType={metricType}
                fontColor={fontColor}
                enableDarkTheme={enableDarkTheme}
                showWarningLegend
              />
            </Box>

            {/* Right side: Both switches + view toggle grouped */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              {/* View toggle: Combined <-> Dashboard */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, val) => {
                  if (val !== null) setViewMode(val);
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    padding: '4px 8px',
                    color: palette.textPrimary,
                    borderColor: enableDarkTheme ? '#2F445E' : '#c4cdd6',
                    '&.Mui-selected': {
                      backgroundColor: enableDarkTheme ? '#2F445E' : '#354A6E',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: enableDarkTheme
                          ? '#3a5470'
                          : '#4a6490',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="combined">
                  <Tooltip title={t('combined_view_pressure_temperature')}>
                    <LayersIcon sx={{ fontSize: '16px' }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="dashboard">
                  <Tooltip title={t('dashboard_view')}>
                    <TableChartIcon sx={{ fontSize: '16px' }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              {/* Grouping switch: All <-> Unhealthy Assets */}
              <Tooltip title={t('show_only_assets_having_unhealthy_tires')}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: 0.25,
                  }}
                >
                  <span
                    style={{
                      color: fontColor,
                      fontSize: '11px',
                      marginRight: 6,
                    }}
                  >
                    {t('all')}
                  </span>
                  <Switch
                    size="small"
                    checked={assetGrouping === 'Unhealthy Assets'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setAssetGrouping(
                        e.target.checked ? 'Unhealthy Assets' : 'All'
                      );
                    }}
                    color="secondary"
                    inputProps={{
                      'aria-label': t(
                        'toggle_assets_with_unhealthy_tires_only'
                      ),
                    }}
                  />
                  <span
                    style={{
                      color: fontColor,
                      fontSize: '11px',
                      marginLeft: 6,
                    }}
                  >
                    {t('unhealthy_assets')}
                  </span>
                </Box>
              </Tooltip>
            </Box>
          </Box>
          <Box
            display={'flex'}
            alignItems={'center'}
            margin={'8px 0px'}
            justifyContent={'space-between'}
          >
            {viewMode === 'combined' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 26,
                    borderRadius: '3px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.2)',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#6ea8d8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 13,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '8px',
                        color: '#fff',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {unitSystem === 'metric' ? 'KPA' : 'PSI'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }}
                  />
                  <Box
                    sx={{
                      backgroundColor: '#e89c6a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 13,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '8px',
                        color: '#fff',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {unitSystem === 'metric' ? '°C' : '°F'}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '2px',
                        backgroundColor: '#6ea8d8',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: '11px', color: fontColor, lineHeight: 1 }}
                    >
                      {t('pressure_top')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '2px',
                        backgroundColor: '#e89c6a',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: '11px', color: fontColor, lineHeight: 1 }}
                    >
                      {t('temperature_bottom')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box />
            )}
            <ReportDownload />
          </Box>

          {viewMode === 'dashboard' ? (
            <>
              <Suspense fallback={null}>
                <FleetAlertSummary
                  notifications={dashboard?.notifications ?? []}
                  leakageNotifications={
                    dashboard?.tpmsLeakageNotifications ?? []
                  }
                  loading={isLoadingDashboard}
                  enableDarkTheme={enableDarkTheme}
                  alertFilter={alertFilter}
                  onAlertFilterChange={(key) => {
                    setAlertFilter((prev) => (prev === key ? null : key));
                  }}
                />
              </Suspense>
              <FleetDashboardView
                fleetList={fleetList}
                fleetData={fleetData}
                axleWiseLimits={axleWiseLimits}
                unitSystem={unitSystem}
                makeModelDetails={makeModelDetails}
                tirePatterns={tirePatterns}
                customerId={customerId}
                enableDarkTheme={enableDarkTheme}
                fontColor={fontColor}
                metricType={metricType}
                batteryData={batteryData}
                notifications={dashboard?.notifications ?? []}
                leakageNotifications={dashboard?.tpmsLeakageNotifications ?? []}
                alertFilter={alertFilter}
              />
            </>
          ) : fleetList.length > 0 ? (
            <Grid container spacing={2}>
              {fleetList?.map((item, index) => {
                const deviceId = item.device?.deviceId;
                const itemData =
                  deviceId != null
                    ? fleetData?.[metricType]?.[deviceId] ?? {}
                    : {};
                const pressureData =
                  deviceId != null ? fleetData?.pressure?.[deviceId] ?? {} : {};
                const temperatureItemData =
                  deviceId != null
                    ? fleetData?.temperature?.[deviceId] ?? {}
                    : {};
                const dataKeys = Object.keys(itemData);
                const dataValues = Object.values(itemData);
                const assetTireDetails = getDynamicAssetTireInfo(
                  makeModelDetails,
                  tirePatterns.tirePatternPositions,
                  item.make,
                  item.model,
                  customerId
                );
                const itemPattern =
                  item.patternName ?? assetTireDetails.patternName;
                const pngName = getImageName(item, 'Small');

                const orderedKeys = getOrderedTireCodes(dataKeys, itemPattern);

                const lastUpdated = getLastUpdatedTimestamp(dataValues);

                // Build per-tire display values for combined view
                const pressureDisplayValues: Record<string, string | null> = {};
                const temperatureDisplayValues: Record<string, string | null> =
                  {};
                orderedKeys.forEach((key, idx) => {
                  const posKey = `p${idx + 1}`;
                  const pEntry = (pressureData as any)[key];
                  const tEntry = (temperatureItemData as any)[key];
                  pressureDisplayValues[posKey] =
                    pEntry?.value !== null && pEntry?.value !== undefined
                      ? String(Math.round(Number(pEntry.value)))
                      : null;
                  temperatureDisplayValues[posKey] =
                    tEntry?.value !== null && tEntry?.value !== undefined
                      ? String(Math.round(Number(tEntry.value)))
                      : null;
                });

                const assetsWithLiveData = addLiveDataToAssets(
                  assets,
                  latestTagData
                );

                return (
                  <Grid
                    item
                    key={index}
                    lg={2.4}
                    md={3}
                    sm={4}
                    xs={6}
                    marginBottom={'12px'}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        border: '1px solid #2F445E',
                        borderRadius: '6px',
                        padding: '8px',
                        cursor: lastUpdated !== 'N/A' ? 'pointer' : '',
                        transition: 'all 0.2s ease-in-out',
                        opacity: lastUpdated === 'N/A' ? 0.6 : 1,
                        backgroundColor: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.02)'
                          : 'rgba(0, 0, 0, 0.02)',
                        ...(lastUpdated !== 'N/A' && {
                          '&:hover': {
                            opacity: 0.8,
                            transform: 'translateY(-2px)',
                          },
                        }),
                      }}
                      onClick={() => {
                        if (lastUpdated !== 'N/A') {
                          navigate(
                            `/tire-insights/vehicle-insight?asset=${item.bumperNumber}&metric=Pressure`
                          );
                        }
                      }}
                    >
                      {/** Asset Data */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <PNG
                            name={pngName}
                            alt={item.bumperNumber}
                            style={{
                              height: '60px',
                              width: '76px',
                              marginTop: '5px',
                            }}
                          />
                          <Box
                            sx={{
                              marginTop: '8px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: '700',
                                color: fontColor,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'left',
                                }}
                              >
                                {item.bumperNumber}
                                <Tooltip
                                  title={t('last_tpms_update', {
                                    time:
                                      lastUpdated !== 'N/A'
                                        ? convertUtcToDisplayTime(
                                            new Date(lastUpdated).toISOString(),
                                            preferredTimeDisplayMode,
                                            timezone,
                                            DATE_TIME_FORMAT
                                          )
                                        : lastUpdated,
                                  })}
                                >
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      marginLeft: '5px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <SVG
                                      name="infoTooltipIcon"
                                      style={{
                                        width: '14px',
                                        height: '14px',
                                        opacity: 0.7,
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              </Box>
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: fontColor,
                              }}
                            >
                              {item.make} - {item.model}
                            </Typography>
                            {lastUpdated === 'N/A' && (
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: fontColor,
                                }}
                              >
                                {t('sensors_not_available')}{' '}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      {/** Tire Data */}
                      <Box
                        sx={{
                          marginTop: '10px',
                        }}
                      >
                        {dataKeys.length > 0 ? (
                          <Box
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            {(() => {
                              const deviceId = item.device?.deviceId;
                              const deviceBattery = deviceId
                                ? batteryData?.[deviceId]
                                : undefined;
                              let batteryLowMap: Record<string, boolean> = {};
                              if (itemPattern === '10x6') {
                                batteryLowMap = {
                                  p1: !!deviceBattery?.['0x17']?.isBatteryLow,
                                  p2: !!deviceBattery?.['0x19']?.isBatteryLow,
                                  p3: !!deviceBattery?.['0x27']?.isBatteryLow,
                                  p4: !!deviceBattery?.['0x29']?.isBatteryLow,
                                  p5: !!deviceBattery?.['0x26']?.isBatteryLow,
                                  p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
                                  p7: !!deviceBattery?.['0x37']?.isBatteryLow,
                                  p8: !!deviceBattery?.['0x39']?.isBatteryLow,
                                  p9: !!deviceBattery?.['0x36']?.isBatteryLow,
                                  p10: !!deviceBattery?.['0x3A']?.isBatteryLow,
                                };
                              } else if (itemPattern === '6x6') {
                                batteryLowMap = {
                                  p1: !!deviceBattery?.['0x17']?.isBatteryLow,
                                  p2: !!deviceBattery?.['0x19']?.isBatteryLow,
                                  p3: !!deviceBattery?.['0x27']?.isBatteryLow,
                                  p4: !!deviceBattery?.['0x29']?.isBatteryLow,
                                  p5: !!deviceBattery?.['0x37']?.isBatteryLow,
                                  p6: !!deviceBattery?.['0x39']?.isBatteryLow,
                                };
                              } else if (itemPattern === '6x4') {
                                batteryLowMap = {
                                  p1: !!deviceBattery?.['0x17']?.isBatteryLow,
                                  p2: !!deviceBattery?.['0x19']?.isBatteryLow,
                                  p3: !!deviceBattery?.['0x27']?.isBatteryLow,
                                  p4: !!deviceBattery?.['0x29']?.isBatteryLow,
                                  p5: !!deviceBattery?.['0x26']?.isBatteryLow,
                                  p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
                                };
                              } else if (itemPattern === '4x6') {
                                batteryLowMap = {
                                  p1: !!deviceBattery?.['0x16']?.isBatteryLow,
                                  p2: !!deviceBattery?.['0x17']?.isBatteryLow,
                                  p3: !!deviceBattery?.['0x19']?.isBatteryLow,
                                  p4: !!deviceBattery?.['0x1A']?.isBatteryLow,
                                  p5: !!deviceBattery?.['0x27']?.isBatteryLow,
                                  p6: !!deviceBattery?.['0x29']?.isBatteryLow,
                                };
                              } else {
                                batteryLowMap = {
                                  p1: !!deviceBattery?.['0x17']?.isBatteryLow,
                                  p2: !!deviceBattery?.['0x19']?.isBatteryLow,
                                  p3: !!deviceBattery?.['0x27']?.isBatteryLow,
                                  p4: !!deviceBattery?.['0x29']?.isBatteryLow,
                                };
                              }

                              // Combined view: show both pressure + temperature
                              return itemPattern === '6x4' ? (
                                <CombinedSixWheels
                                  pressureValues={pressureDisplayValues}
                                  temperatureValues={temperatureDisplayValues}
                                  pressureStatusMap={
                                    item.pressureTireStatusMap ?? {}
                                  }
                                  temperatureStatusMap={
                                    item.temperatureTireStatusMap ?? {}
                                  }
                                  batteryLow={batteryLowMap}
                                  tireStaleMap={item.tireStaleMap}
                                />
                              ) : itemPattern === '4x6' ? (
                                <CombinedFourSixWheels
                                  pressureValues={pressureDisplayValues}
                                  temperatureValues={temperatureDisplayValues}
                                  pressureStatusMap={
                                    item.pressureTireStatusMap ?? {}
                                  }
                                  temperatureStatusMap={
                                    item.temperatureTireStatusMap ?? {}
                                  }
                                  batteryLow={batteryLowMap}
                                  tireStaleMap={item.tireStaleMap}
                                />
                              ) : itemPattern === '6x6' ? (
                                <CombinedThreeAxlesSixWheels
                                  pressureValues={pressureDisplayValues}
                                  temperatureValues={temperatureDisplayValues}
                                  pressureStatusMap={
                                    item.pressureTireStatusMap ?? {}
                                  }
                                  temperatureStatusMap={
                                    item.temperatureTireStatusMap ?? {}
                                  }
                                  batteryLow={batteryLowMap}
                                  tireStaleMap={item.tireStaleMap}
                                />
                              ) : itemPattern === '10x6' ? (
                                <CombinedThreeAxlesTenWheels
                                  pressureValues={pressureDisplayValues}
                                  temperatureValues={temperatureDisplayValues}
                                  pressureStatusMap={
                                    item.pressureTireStatusMap ?? {}
                                  }
                                  temperatureStatusMap={
                                    item.temperatureTireStatusMap ?? {}
                                  }
                                  batteryLow={batteryLowMap}
                                  tireStaleMap={item.tireStaleMap}
                                />
                              ) : (
                                <CombinedFourWheels
                                  pressureValues={pressureDisplayValues}
                                  temperatureValues={temperatureDisplayValues}
                                  pressureStatusMap={
                                    item.pressureTireStatusMap ?? {}
                                  }
                                  temperatureStatusMap={
                                    item.temperatureTireStatusMap ?? {}
                                  }
                                  batteryLow={batteryLowMap}
                                  tireStaleMap={item.tireStaleMap}
                                />
                              );
                            })()}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              color: fontColor,
                              fontSize: '14px',
                            }}
                          >
                            {' '}
                            {t('no_tire_data')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <>
              {assetGrouping !== 'Unhealthy Assets' ? (
                <Typography
                  sx={{
                    color: fontColor,
                  }}
                >
                  {' '}
                  {t('no_card_data_tpms_hint')}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    color: fontColor,
                  }}
                >
                  {' '}
                  {t('no_assets_available')}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default FleetInsight;
